import { isGADebugMode, trackEvent } from "./ga";
import {
  COMMANDER_EVENT_PREFIX,
  COMMANDER_MODULES,
  CommanderModuleId,
} from "./catalog/modules";
import {
  REDEMPTION_ACTIONS,
  RedemptionActionId,
} from "./catalog/redemptionActions";
import {
  DAILY_TASKS_ACTIONS,
  DailyTasksActionId,
} from "./catalog/dailyTasksActions";
import { OPS_TAT_ACTIONS, OpsTatActionId } from "./catalog/opsTatActions";
import { ALERTS_ACTIONS, AlertsActionId } from "./catalog/alertsActions";
import {
  ALERTS_DASHBOARD_ACTIONS,
  AlertsDashboardActionId,
} from "./catalog/alertsDashboardActions";

/** Kept for docs / Explore filters via params.module_id — not fired as a separate event. */
export const COMMANDER_DASHBOARD_EVENT = "commander_dashboard_action";

/** GA4 event names max 40 chars; must be [a-zA-Z][a-zA-Z0-9_]* */
const GA_EVENT_NAME_MAX = 40;

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;

/**
 * Params that duplicate readable text or blow past the useful GA custom-dim budget.
 * Callers may still pass them; they are dropped before send.
 */
const BLOCKED_CONTEXT_KEYS = new Set([
  "action_name",
  "action_description",
  "action_detail",
  "action_detail_full",
  "filter_summary",
  "order_ids",
  "customer_ids",
  "date_range_label",
  "screen_id",
  "screen_path",
  "has_order_filter",
  "has_customer_filter",
  "event_label",
]);

export type CommanderActionContext = Record<
  string,
  string | number | boolean | null | undefined
>;

type TrackCommanderActionInput =
  | {
      moduleId: "product_redemptions";
      actionId: RedemptionActionId;
      context?: CommanderActionContext;
      /** Short GA4 event name shown in DebugView timeline (max 40 chars). */
      eventName?: string;
    }
  | {
      moduleId: "daily_tasks";
      actionId: DailyTasksActionId;
      context?: CommanderActionContext;
      eventName?: string;
    }
  | {
      moduleId: "ops_tat";
      actionId: OpsTatActionId;
      context?: CommanderActionContext;
      eventName?: string;
    }
  | {
      moduleId: "alerts";
      actionId: AlertsActionId;
      context?: CommanderActionContext;
      eventName?: string;
    }
  | {
      moduleId: "alerts_dashboard";
      actionId: AlertsDashboardActionId;
      context?: CommanderActionContext;
      eventName?: string;
    }
  | {
      moduleId: CommanderModuleId;
      actionId: string;
      context?: CommanderActionContext;
      eventName?: string;
    };

/**
 * Looks up a catalog action for the given module.
 *
 * @param moduleId - Commander module id.
 * @param actionId - Stable action id within that module.
 * @returns Catalog name/description, or null when unknown.
 */
const resolveAction = (
  moduleId: CommanderModuleId,
  actionId: string,
): { name: string; description: string } | null => {
  if (moduleId === "product_redemptions") {
    const action = REDEMPTION_ACTIONS[actionId as RedemptionActionId];
    if (action) {
      return action;
    }
  }
  if (moduleId === "daily_tasks") {
    const action = DAILY_TASKS_ACTIONS[actionId as DailyTasksActionId];
    if (action) {
      return action;
    }
  }
  if (moduleId === "ops_tat") {
    const action = OPS_TAT_ACTIONS[actionId as OpsTatActionId];
    if (action) {
      return action;
    }
  }
  if (moduleId === "alerts") {
    const action = ALERTS_ACTIONS[actionId as AlertsActionId];
    if (action) {
      return action;
    }
  }
  if (moduleId === "alerts_dashboard") {
    const action =
      ALERTS_DASHBOARD_ACTIONS[actionId as AlertsDashboardActionId];
    if (action) {
      return action;
    }
  }
  return null;
};

/**
 * Sanitizes a GA4 event name to the allowed character set and length.
 *
 * @param name - Proposed event name.
 * @returns Safe GA4 event name (max 40 chars).
 */
function sanitizeGaEventName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const withLetter = /^[a-zA-Z]/.test(cleaned) ? cleaned : `e_${cleaned}`;
  return withLetter.slice(0, GA_EVENT_NAME_MAX);
}

/**
 * Truncates string param values to GA4's practical custom-param limit.
 *
 * @param value - Raw context value.
 * @returns Truncated string, or the original number/boolean.
 */
function truncateParamValue(
  value: string | number | boolean,
): string | number | boolean {
  if (typeof value === "string") {
    return value.slice(0, GA_PARAM_STRING_MAX);
  }
  return value;
}

/**
 * Fire a non-blocking Commander dashboard action event for GA4.
 * One event per action (no duplicates). Name is short for DebugView;
 * params carry action_name, action_description, and screen_name for Explore.
 *
 * @param input - Module/action ids, optional short eventName, optional context.
 */
export function trackCommanderAction(input: TrackCommanderActionInput): void {
  try {
    const module = COMMANDER_MODULES[input.moduleId];
    if (!module) {
      return;
    }

    const action = resolveAction(input.moduleId, input.actionId);
    if (!action) {
      console.debug(
        `[GA] Unknown commander action: ${input.moduleId}.${input.actionId}`,
      );
      return;
    }

    const pagePath =
      typeof window !== "undefined" ? window.location.pathname : "";

    const context = input.context || {};
    const actionName =
      typeof context.action_name === "string" && context.action_name.trim()
        ? context.action_name.trim()
        : action.name;
    const actionDescription =
      typeof context.action_description === "string" &&
      context.action_description.trim()
        ? context.action_description.trim()
        : action.description;
    const screenName =
      typeof context.screen_name === "string" ? context.screen_name : undefined;

    const prefix =
      COMMANDER_EVENT_PREFIX[input.moduleId] || `${input.moduleId}_db`;
    const eventName = sanitizeGaEventName(
      input.eventName || `${prefix}_${input.actionId}`,
    );

    const params: Record<string, string | number | boolean> = {
      module_id: input.moduleId,
      module_name: module.name,
      action_id: input.actionId,
      action_name: actionName.slice(0, GA_PARAM_STRING_MAX),
      action_description: actionDescription.slice(0, GA_PARAM_STRING_MAX),
      page_path: pagePath.slice(0, GA_PARAM_STRING_MAX),
    };

    Object.entries(context).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (BLOCKED_CONTEXT_KEYS.has(key)) {
        return;
      }
      params[key] = truncateParamValue(value);
    });

    if (isGADebugMode()) {
      console.log(`[GA] ${eventName} · ${actionName}`, {
        screen: screenName,
        description: actionDescription,
        params,
      });
    }

    trackEvent(eventName, params);
  } catch (error) {
    console.debug("[GA] trackCommanderAction failed", error);
  }
}
